/**
 * Repairs for design SVGs written by the builder's design editor (customers
 * editing their own designs), which are legal-looking but ambiguous:
 *
 * Every trimmed stripe is exported as
 *   <g clip-path="url(#sleeve)"><clipPath id="Stripe-1__trim">…</clipPath>
 *     <rect id="Stripe-1" clip-path="url(#Stripe-1__trim)"/></g>
 * and the SAME clipPath id is reused for every stripe. Browsers resolve
 * `url(#id)` to the first element with that id in the document, fabric's
 * parser to the last — either way two of the three stripes are trimmed by a
 * rectangle that belongs to another panel and vanish (or bleed). The author's
 * intent is unambiguous though: each clipPath is defined right beside its one
 * user, so references are re-pointed to the definition in their own scope.
 */

export interface DedupeClipPathIdsResult {
  svg: string;
  /** `oldId->newId` for every clipPath that was renamed. */
  renamed: string[];
}

const CLIP_TAGS = new Set(['clippath']);

function isClipWithId(el: Element, id: string): boolean {
  return CLIP_TAGS.has(el.tagName.toLowerCase()) && el.getAttribute('id') === id;
}

/** The element whose subtree a clipPath definition belongs to: its parent, or the parent of its `<defs>`. */
function scopeOf(clip: Element): Element | null {
  const parent = clip.parentElement;
  if (!parent) return null;
  return parent.tagName.toLowerCase() === 'defs' ? parent.parentElement : parent;
}

/** Does `scope` define a clipPath `id` directly (as a child, or inside a child `<defs>`)? */
function definesClip(scope: Element, id: string): boolean {
  return Array.from(scope.children).some(
    (child) =>
      isClipWithId(child, id) ||
      (child.tagName.toLowerCase() === 'defs' &&
        Array.from(child.children).some((c) => isClipWithId(c, id)))
  );
}

/** Nearest ancestor of `el` (inclusive of the root) that defines clipPath `id`. */
function definingScope(el: Element, id: string): Element | null {
  let node: Element | null = el.parentElement;
  while (node) {
    if (definesClip(node, id)) return node;
    node = node.parentElement;
  }
  return null;
}

function referencesClip(el: Element, id: string): boolean {
  const ref = `url(#${id})`;
  return (
    (el.getAttribute('clip-path') ?? '').includes(ref) ||
    (el.getAttribute('style') ?? '').includes(ref)
  );
}

function repointClip(el: Element, oldId: string, newId: string): void {
  const from = `url(#${oldId})`;
  const to = `url(#${newId})`;
  const attr = el.getAttribute('clip-path');
  if (attr?.includes(from)) el.setAttribute('clip-path', attr.split(from).join(to));
  const style = el.getAttribute('style');
  if (style?.includes(from)) el.setAttribute('style', style.split(from).join(to));
}

/**
 * Give every duplicated clipPath id a unique one and re-point the references
 * in that definition's own scope to it. Duplicates that share a scope keep
 * browser semantics (the first definition wins the scope's references).
 * Documents without duplicates are returned untouched (same string).
 */
export function dedupeClipPathIds(svgText: string): DedupeClipPathIdsResult {
  const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
  if (doc.getElementsByTagName('parsererror').length) return { svg: svgText, renamed: [] };

  const byId = new Map<string, Element[]>();
  for (const clip of Array.from(doc.getElementsByTagName('clipPath'))) {
    const id = clip.getAttribute('id');
    if (id) byId.set(id, [...(byId.get(id) ?? []), clip]);
  }

  // Decide everything against the ORIGINAL ids, then mutate.
  const plan: Array<{ clip: Element; id: string; newId: string; refs: Element[] }> = [];
  byId.forEach((clips, id) => {
    if (clips.length < 2) return;
    const claimed = new Set<Element>();
    clips.forEach((clip, n) => {
      const scope = scopeOf(clip);
      const newId = `${id}__u${n + 1}`;
      const refs: Element[] = [];
      if (scope && !claimed.has(scope)) {
        claimed.add(scope);
        for (const el of Array.from(scope.querySelectorAll('[clip-path],[style]'))) {
          if (referencesClip(el, id) && definingScope(el, id) === scope) refs.push(el);
        }
      }
      plan.push({ clip, id, newId, refs });
    });
  });
  if (!plan.length) return { svg: svgText, renamed: [] };

  const renamed: string[] = [];
  for (const { clip, id, newId, refs } of plan) {
    clip.setAttribute('id', newId);
    refs.forEach((el) => repointClip(el, id, newId));
    renamed.push(`${id}->${newId}`);
  }
  return { svg: new XMLSerializer().serializeToString(doc), renamed };
}
